import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import { companyInfo } from '../../utils/dataInfo';
import { getPaymentMode } from '../../utils/helpers';

// Helper function to get branch name
const getBranchName = (branchId, branchesData) => {
  if (!branchesData || !Array.isArray(branchesData)) return 'Unknown';
  const branch = branchesData.find(b => parseInt(b.id) === parseInt(branchId));
  return branch ? branch.name : 'Unknown';
};

// Helper function to get customer name
const getCustomerName = (customerId, customerData) => {
  if (!customerData || !Array.isArray(customerData)) return 'Unknown';
  const customer = customerData.find(c => parseInt(c.id) === parseInt(customerId));
  return customer ? customer.customer_name : 'Unknown';
};

// Helper function to get status name
const getStatusName = (status) => {
  switch(parseInt(status)) {
    case 1: return 'Active';
    case 0: return 'Inactive';
    default: return 'Unknown';
  }
};

// Helper function to format currency
const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return '0';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount) + " "+"sh";
};

// Helper function to format date
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

// Helper function to split text into multiple lines for PDF
const splitTextIntoLines = (doc, text, maxWidth) => {
  if (!text) return [''];
  const words = text.split(' ');
  const lines = [];
  let currentLine = words[0];

  for (let i = 1; i < words.length; i++) {
    const word = words[i];
    const width = doc.getTextWidth(currentLine + ' ' + word);
    if (width < maxWidth) {
      currentLine += ' ' + word;
    } else {
      lines.push(currentLine);
      currentLine = word;
    }
  }
  lines.push(currentLine);
  return lines;
};

// Manual table drawing function for Gym Bookings PDF (Landscape mode)
const drawGymTable = (doc, data, startY, pageWidth) => {
  const margin = 14;
  const rowHeight = 12;

  // Column widths: #, Number, Date, Customer, Period, Total, Paid, Balance, Payment, Status, Branch
  const colWidths = [10, 40, 25, 35, 20, 20, 20, 20, 30, 20, 25];

  let currentY = startY;
  const neededHeight = (data.length + 1) * rowHeight;
  
  if (currentY + neededHeight > doc.internal.pageSize.height - 20) {
    doc.addPage('l');
    currentY = margin;
  }

  // Draw table header
  doc.setFillColor(255, 255, 255);
  doc.setTextColor(25, 118, 210);
  doc.setFont(undefined, 'bold');

  const headers = ['#', 'Number', 'Date', 'Customer', 'Period', 'Total', 'Paid', 'Balance', 'Payment', 'Status', 'Branch'];
  let xPos = margin;

  // Draw header background
  doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');

  // Draw header text
  headers.forEach((header, index) => {
    const cellX = xPos + 2;
    doc.text(header, cellX, currentY + 7);
    xPos += colWidths[index];
  });

  // Draw header underline
  doc.setDrawColor(25, 118, 210);
  doc.setLineWidth(0.5);
  doc.line(margin, currentY + rowHeight, pageWidth - margin, currentY + rowHeight);

  currentY += rowHeight;

  // Draw table rows
  doc.setFont(undefined, 'normal');
  doc.setTextColor(0, 0, 0);

  data.forEach((row, rowIndex) => {
    let maxLines = 1;
    const cellLines = row.map((cell, cellIndex) => {
      const text = String(cell);
      const maxWidth = colWidths[cellIndex] - 4;
      const lines = splitTextIntoLines(doc, text, maxWidth);
      maxLines = Math.max(maxLines, lines.length);
      return lines;
    });

    const actualRowHeight = rowHeight * maxLines;

    if (currentY + actualRowHeight > doc.internal.pageSize.height - 20) {
      doc.addPage('l');
      currentY = margin;
      
      doc.setFillColor(255, 255, 255);
      doc.setTextColor(25, 118, 210);
      doc.setFont(undefined, 'bold');
      
      doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');
      
      xPos = margin;
      headers.forEach((header, index) => {
        doc.text(header, xPos + 2, currentY + 7);
        xPos += colWidths[index];
      });
      
      doc.setDrawColor(25, 118, 210);
      doc.line(margin, currentY + rowHeight, pageWidth - margin, currentY + rowHeight);
      
      currentY += rowHeight;
      doc.setFont(undefined, 'normal');
      doc.setTextColor(0, 0, 0);
    }
    
    // Alternate row colors
    if (rowIndex % 2 === 0) {
      doc.setFillColor(248, 248, 248);
    } else {
      doc.setFillColor(255, 255, 255);
    }
    
    // Draw row background
    doc.rect(margin, currentY, pageWidth - (2 * margin), actualRowHeight, 'F');
    
    // Draw row text
    cellLines.forEach((lines, cellIndex) => {
      xPos = margin + colWidths.slice(0, cellIndex).reduce((sum, width) => sum + width, 0);
      
      lines.forEach((line, lineIndex) => {
        const lineY = currentY + 7 + (lineIndex * rowHeight);
        doc.text(line, xPos + 2, lineY);
      });
    });
    
    currentY += actualRowHeight;
  });

  return currentY;
};

// Export Gym Bookings to Excel - Show all attributes
export const exportToExcel = (gymRecords, branchesData = [], customerData = [], filters = {}) => {
  try {
    // Ensure gymRecords is an array
    if (!Array.isArray(gymRecords)) {
      throw new Error('Gym records data is not an array');
    }

    // Prepare data for Excel with all attributes
    const excelData = gymRecords.map((record, index) => {
      const safeRecord = record || {};
      
      return {
        'No.': index + 1,
        'ID': safeRecord.id || '',
        'Booking Number': safeRecord.booking_no || '',
        'Date': formatDate(safeRecord.created_at),
        'Customer ID': safeRecord.customer_id || '',
        'Customer Name': getCustomerName(safeRecord.customer_id, customerData),
        'Customer Phone': safeRecord.customer_phone || '',
        'Customer Email': safeRecord.customer_email || '',
        'Customer Address': safeRecord.customer_address || '',
        'Period/Duration': safeRecord.period || '',
        'Total Amount': formatCurrency(safeRecord.total_amount || 0),
        'Paid Amount': formatCurrency(safeRecord.paid_amount || 0),
        'Balance Amount': formatCurrency(safeRecord.balance_amount || 0),
        'Payment Method': getPaymentMode(safeRecord.payment_method),
        'Branch ID': safeRecord.branch_id || '',
        'Branch': getBranchName(safeRecord.branch_id, branchesData),
        'Status': getStatusName(safeRecord.status),
        'Description': safeRecord.description || '',
        'Created Date': formatDate(safeRecord.created_at),
        'Updated Date': formatDate(safeRecord.updated_at)
      };
    });

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    // Auto-size columns
    const maxWidth = excelData.reduce((widths, row) => {
      Object.keys(row).forEach((key, idx) => {
        const length = String(row[key]).length;
        if (!widths[idx] || length > widths[idx]) {
          widths[idx] = length;
        }
      });
      return widths;
    }, []);
    
    worksheet['!cols'] = maxWidth.map(width => ({ width: Math.min(width + 2, 50) }));

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Gym Bookings');

    // Generate Excel file and download
    const fileName = `gym_bookings_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);

    return { success: true, fileName };
  } catch (error) {
    console.error('Gym bookings Excel export failed:', error);
    throw new Error('Failed to export to Excel: ' + error.message);
  }
};

// PDF export for Gym Bookings - Show only selected columns in Landscape mode
export const exportToPDF = (gymRecords, branchesData = [], customerData = [], filters = {}) => {
  try {
    // Ensure gymRecords is an array
    if (!Array.isArray(gymRecords)) {
      throw new Error('Gym records data is not an array');
    }

    // Create PDF in landscape mode
    const doc = new jsPDF('l', 'mm', 'a4');
    
    // Set document properties
    doc.setProperties({
      title: 'Gym Bookings Report',
      subject: 'Gym Bookings Export',
      author: companyInfo.name,
      keywords: 'gym, bookings, export, report',
      creator: companyInfo.name
    });
    
    // Get landscape page width and height
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    
    // Title section
    doc.setFontSize(20);
    doc.setTextColor(25, 118, 210);
    doc.text('GYM BOOKINGS REPORT', pageWidth / 2, 25, { align: 'center' });
    
    // Generation details
    doc.setFontSize(10);
    doc.setTextColor(80, 80, 80);
    let currentY = 35;
    doc.text(`Report Generated: ${new Date().toLocaleString()}`, 14, currentY);
    currentY += 6;
    doc.text(`Total Bookings: ${gymRecords.length}`, 14, currentY);
    currentY += 6;
    
    // Filters applied
    if (filters && (filters.customer_id || filters.payment_method || 
        filters.status || filters.dateFrom || filters.dateTo)) {
      doc.text('Applied Filters:', 14, currentY);
      currentY += 6;
      
      if (filters.customer_id) {
        doc.text(`• Customer: ${getCustomerName(filters.customer_id, customerData)}`, 20, currentY);
        currentY += 6;
      }
      if (filters.payment_method) {
        doc.text(`• Payment Method: ${getPaymentMode(filters.payment_method)}`, 20, currentY);
        currentY += 6;
      }
      if (filters.status) {
        doc.text(`• Status: ${getStatusName(filters.status)}`, 20, currentY);
        currentY += 6;
      }
      if (filters.dateFrom) {
        doc.text(`• From date: ${formatDate(filters.dateFrom)}`, 20, currentY);
        currentY += 6;
      }
      if (filters.dateTo) {
        doc.text(`• To date: ${formatDate(filters.dateTo)}`, 20, currentY);
        currentY += 6;
      }
      currentY += 6;
    }
    
    // Prepare table data with only: #, Number, Date, Customer, Period, Total, Paid, Balance, Payment, Status, Branch
    const tableData = gymRecords.map((record, index) => {
      const safeRecord = record || {};
      return [
        (index + 1).toString(),
        safeRecord.booking_no || '',
        formatDate(safeRecord.created_at),
        getCustomerName(safeRecord.customer_id, customerData),
        safeRecord.period || '',
        formatCurrency(safeRecord.total_amount || 0),
        formatCurrency(safeRecord.paid_amount || 0),
        formatCurrency(safeRecord.balance_amount || 0),
        getPaymentMode(safeRecord.payment_method),
        getStatusName(safeRecord.status),
        getBranchName(safeRecord.branch_id, branchesData)
      ];
    });
    
    // Draw the table
    drawGymTable(doc, tableData, currentY + 5, pageWidth);
    
    const fileName = `gym_bookings_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);

    return { success: true, fileName };
  } catch (error) {
    console.error('Gym bookings PDF export failed:', error);
    throw new Error('Failed to export to PDF: ' + error.message);
  }
};

// CSV export for Gym Bookings - Show all attributes
export const exportToCSV = (gymRecords, branchesData = [], customerData = []) => {
  try {
    // Ensure gymRecords is an array
    if (!Array.isArray(gymRecords)) {
      throw new Error('Gym records data is not an array');
    }

    const headers = [
      'ID',
      'Booking Number',
      'Date',
      'Customer ID',
      'Customer Name',
      'Customer Phone',
      'Customer Email',
      'Customer Address',
      'Period/Duration',
      'Total Amount',
      'Paid Amount',
      'Balance Amount',
      'Payment Method',
      'Branch ID',
      'Branch',
      'Status',
      'Description',
      'Created Date',
      'Updated Date'
    ];
    
    const csvData = gymRecords.map(record => {
      const safeRecord = record || {};
      
      return [
        safeRecord.id || '',
        safeRecord.booking_no || '',
        formatDate(safeRecord.created_at),
        safeRecord.customer_id || '',
        `"${getCustomerName(safeRecord.customer_id, customerData).replace(/"/g, '""')}"`,
        `"${(safeRecord.customer_phone || '').replace(/"/g, '""')}"`,
        `"${(safeRecord.customer_email || '').replace(/"/g, '""')}"`,
        `"${(safeRecord.customer_address || '').replace(/"/g, '""')}"`,
        `"${(safeRecord.period || '').replace(/"/g, '""')}"`,
        safeRecord.total_amount || 0,
        safeRecord.paid_amount || 0,
        safeRecord.balance_amount || 0,
        `"${getPaymentMode(safeRecord.payment_method).replace(/"/g, '""')}"`,
        safeRecord.branch_id || '',
        `"${getBranchName(safeRecord.branch_id, branchesData).replace(/"/g, '""')}"`,
        `"${getStatusName(safeRecord.status).replace(/"/g, '""')}"`,
        `"${(safeRecord.description || '').replace(/"/g, '""')}"`,
        formatDate(safeRecord.created_at),
        formatDate(safeRecord.updated_at)
      ];
    });

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `gym_bookings_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    return { success: true };
  } catch (error) {
    console.error('Gym bookings CSV export failed:', error);
    throw new Error('Failed to export to CSV: ' + error.message);
  }
};